import { useEffect, useState } from 'react';

type ReviewItemType = {
  id: number;
  author: string;
  title: string;
  desc: string;
  stars: number;
  avatar?: string;
};

interface AvvvatarsProps {
  value: string;
}

const Avvvatars: React.FC<AvvvatarsProps> = ({ value }) => (
  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
    {value
      .split(' ')
      .map(name => name[0])
      .join('')}
  </div>
);

interface GoldStarsProps {
  stars: number;
}

const GoldStars: React.FC<GoldStarsProps> = ({ stars }) => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${i < stars ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.799-2.034c-.784-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

const ReviewItems: ReviewItemType[] = [
  {
    id: 1,
    author: 'Uchenna Ogbonnaya',
    title: "I'd rate them a 5/5!",
    stars: 5,
    desc: "With Madina Lifestyle, I've been able to buy simple groceries, or toiletries just with a click. I must say, their service is top-notch, and I will 10/10 be using this app again.",
    avatar: 'images/Image.png'
  },
  {
    id: 2,
    author: 'Maria Olawale',
    title: 'Great Customer Service, Great Items',
    stars: 4,
    desc: 'Everything I ordered from this website came intact without any complaints on my end. I enjoyed every step of the process, from breezing the catalogues to ordering and receiving. Their customer service system is great.',
    avatar: 'images/Image2.png'
  },
  {
    id: 3,
    author: 'Caleb Ogonor',
    title: 'I rate this brand 100%',
    stars: 4,
    desc: "I've done some online shopping in the past, but with this website, nothing lags, and you can track your order in real time. It's been such a lifesaver for me.",
    avatar: 'images/Image3.png'
  },
  {
    id: 4,
    author: 'Chidera Okeke',
    title: 'I love the app',
    stars: 5,
    desc: 'I love the app, and I love the fact that I can order groceries and have them delivered to my doorstep. The customer service is also great, and they are always willing to help.'
  }
];

export default function Reviews() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const nextSlide = () => {
    setCurrentIndex(prevIndex => (prevIndex === ReviewItems.length - 1 ? 0 : prevIndex + 1));
  };

  const prevSlide = () => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? ReviewItems.length - 1 : prevIndex - 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) nextSlide();
    if (touchStart - touchEnd < -50) prevSlide();
  };

  return (
    <div className="w-full py-16 px-4">
      <div className="text-center mb-10">
        <h1 className="text-[#22B872] text-xl font-semibold">Reviews</h1>
        <p className="text-3xl font-semibold mt-2">
          We Care About Your <span className="text-blue-600">Concerns...</span>
        </p>
        <p className="text-gray-600 mt-4">Here what everyone has to say about us and how we responded</p>
      </div>

      <div className="flex justify-center items-center mt-4 space-x-2">
        <div className="flex -space-x-4">
          {ReviewItems.map((_, idx) => (
            <img
              key={idx}
              className={`w-10 h-10 rounded-full border-2 border-white ${
                currentIndex === idx ? 'border-blue-600' : ''
              }`}
              src={_.avatar || 'images/Image.png'}
              alt="Avatar"
            />
          ))}
        </div>
        <p className="text-gray-600 ml-2">Trusted by everyone</p>
      </div>

      <div className="max-w-4xl mx-auto mt-8 border border-dashed border-blue-400 rounded-xl p-6 bg-[#f8f9fb] relative overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}>
          {ReviewItems.map((review, index) => (
            <div key={index} className="w-full flex-shrink-0" style={{ minWidth: '100%' }}>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-start gap-4 mb-4">
                  <Avvvatars value={review.author} />
                  <div>
                    <p className="font-semibold">{review.author}</p>
                    <p className="text-sm text-gray-500">{review.title}</p>
                  </div>
                  <div className="ml-auto">
                    <GoldStars stars={review.stars} />
                  </div>
                </div>
                <p className="text-gray-700">{review.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={prevSlide}
            className="w-10 h-10 rounded-full border border-blue-600 text-blue-600 flex items-center justify-center hover:bg-blue-50 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="w-10 h-10 rounded-full border border-blue-600 text-blue-600 flex items-center justify-center hover:bg-blue-50 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* <div className="flex justify-center mt-4">
          {ReviewItems.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`mx-1 w-3 h-3 rounded-full ${currentIndex === idx ? 'bg-blue-600' : 'bg-gray-300'}`}
            />
          ))}
        </div> */}
      </div>
    </div>
  );
}
